import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Chip,
	TextField,
	Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { useSnackbar } from '@Providers/useSnackbar';
import usePlatformMode from '@Hooks/usePlatformMode';
import useSubmissionsScope from '../Hooks/useSubmissionsScope';
import {
	BreakdownInstructor,
	BreakdownProduct,
	BreakdownResponse,
	fetchSubmissionsBreakdown,
} from '../Services/submissions.req';

/**
 * Pending-work breakdown: one call returns everything grouped instructor →
 * product, so the accordions are purely presentational (no per-instructor
 * fetching). Admin sees instructor accordions; the instructor portal is
 * server-pinned to one instructor and renders its products flat. Counts are
 * clickable — they land on the Queue with the matching filters pre-applied
 * via query params (see parseQueueParams in Submissions.tsx).
 */
const BreakdownTab: React.FC = () => {
	const { isAdminPortal, canView } = useSubmissionsScope();
	const { classroomsEnabled, standaloneEnabled } = usePlatformMode();
	const { displaySnackbar } = useSnackbar();
	const history = useHistory();
	const [range, setRange] = useState<{ from?: string; to?: string }>({});
	const [breakdown, setBreakdown] = useState<BreakdownResponse>();

	const load = useCallback(() => {
		fetchSubmissionsBreakdown(range)
			.then(setBreakdown)
			.catch((error: any) => {
				displaySnackbar('error', error?.response?.data?.message ?? 'Unable to load the breakdown');
			});
	}, [range, displaySnackbar]);

	useEffect(() => {
		if (canView) load();
	}, [canView, load]);

	// Both families enabled = show the family tag per row; a single-family
	// platform collapses that level. Legacy rows of a disabled family are
	// hidden here (the Queue chips still count them — documented asymmetry).
	const familyEnabled = (product: BreakdownProduct) =>
		product.product_type === 'classroom' ? classroomsEnabled : standaloneEnabled;
	const showFamilyTag = classroomsEnabled && standaloneEnabled;

	const openQueue = (product: BreakdownProduct, instructorId: number | undefined, kind: 'test' | 'assignment') => {
		const params = new URLSearchParams();
		params.set('kind', kind);
		if (isAdminPortal && instructorId) params.set('instructor_id', String(instructorId));
		params.set('product_type', product.product_type);
		params.set('product_id', String(product.product_id));
		if (range.from) params.set('from', range.from);
		if (range.to) params.set('to', range.to);
		history.push(`/dashboard/submissions/queue?${params.toString()}`);
	};

	if (!canView) {
		return (
			<Wrapper>
				<Typography variant="h6">Submissions</Typography>
				<Notice>You do not have permission to view submissions.</Notice>
			</Wrapper>
		);
	}

	const instructors = breakdown?.instructors ?? [];
	const visibleProducts = (entry: BreakdownInstructor) => entry.products.filter(familyEnabled);

	const productRow = (entry: BreakdownInstructor, product: BreakdownProduct) => (
		<ProductRow key={`${product.product_type}-${product.product_id}`}>
			<ProductTitle>
				<Typography fontWeight={600}>
					{product.title}
					{product.label ? ` (${product.label})` : ''}
				</Typography>
				{showFamilyTag && (
					<FamilyTag
						size="small"
						variant="outlined"
						label={product.product_type === 'classroom' ? 'Classroom' : 'Standalone'}
					/>
				)}
			</ProductTitle>
			<Counts>
				<CountChip
					size="small"
					label={`Essays: ${product.pending_tests}`}
					onClick={() => openQueue(product, entry.instructor_id, 'test')}
				/>
				<CountChip
					size="small"
					label={`Assignments: ${product.pending_assignments}`}
					onClick={() => openQueue(product, entry.instructor_id, 'assignment')}
				/>
			</Counts>
		</ProductRow>
	);

	return (
		<Wrapper>
			<Header>
				<Typography variant="h5" fontWeight={700}>
					Submissions breakdown
				</Typography>
				<Chips>
					<Chip
						label={`Essays awaiting review: ${breakdown?.totals.pending_tests ?? '…'}`}
						sx={{ backgroundColor: '#ffab00', color: '#fff', fontWeight: 600 }}
					/>
					<Chip
						label={`Assignments pending: ${breakdown?.totals.pending_assignments ?? '…'}`}
						sx={{ backgroundColor: '#ffab00', color: '#fff', fontWeight: 600 }}
					/>
				</Chips>
			</Header>
			<RangeBar>
				<TextField
					size="small"
					type="date"
					label="From"
					InputLabelProps={{ shrink: true }}
					value={range.from ?? ''}
					onChange={(e) => setRange((r) => ({ ...r, from: e.target.value || undefined }))}
				/>
				<TextField
					size="small"
					type="date"
					label="To"
					InputLabelProps={{ shrink: true }}
					value={range.to ?? ''}
					onChange={(e) => setRange((r) => ({ ...r, to: e.target.value || undefined }))}
				/>
			</RangeBar>
			<Notice as="div">Only instructors and products with pending work are listed.</Notice>
			{instructors.length === 0 && breakdown && <Notice>No pending submissions.</Notice>}
			{isAdminPortal
				? instructors.map((entry) => {
						const products = visibleProducts(entry);
						if (products.length === 0) return null;
						return (
							<Accordion key={entry.instructor_id} TransitionProps={{ mountOnEnter: true, unmountOnExit: true }}>
								<AccordionSummary expandIcon={<ExpandMoreIcon />}>
									<SummaryLine>
										<Typography fontWeight={700}>
											{[entry.first_name, entry.last_name].filter(Boolean).join(' ') ||
												`Instructor #${entry.instructor_id}`}
										</Typography>
										<Typography color="text.secondary">
											{entry.pending_tests} essays · {entry.pending_assignments} assignments
										</Typography>
									</SummaryLine>
								</AccordionSummary>
								<AccordionDetails>{products.map((product) => productRow(entry, product))}</AccordionDetails>
							</Accordion>
						);
				  })
				: visibleProducts(instructors[0] ?? { products: [] } as any).map((product) =>
						productRow(instructors[0], product)
				  )}
		</Wrapper>
	);
};

const Wrapper = styled.div`
	padding: 2rem;
`;

const Header = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	flex-wrap: wrap;
	gap: 1rem;
	margin-bottom: 1rem;
`;

const Chips = styled.div`
	display: flex;
	gap: 0.75rem;
	flex-wrap: wrap;
`;

const RangeBar = styled.div`
	display: flex;
	gap: 0.75rem;
	margin-bottom: 1rem;
`;

const SummaryLine = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	width: 100%;
	padding-right: 1rem;
	gap: 1rem;
`;

const ProductRow = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 1rem;
	padding: 0.5rem 0;
	border-bottom: 1px solid #f0f0f0;
	&:last-child {
		border-bottom: none;
	}
`;

const ProductTitle = styled.div`
	display: flex;
	align-items: center;
	gap: 0.5rem;
`;

const FamilyTag = styled(Chip)`
	font-size: 0.7rem;
`;

const Counts = styled.div`
	display: flex;
	gap: 0.5rem;
`;

const CountChip = styled(Chip)`
	cursor: pointer;
`;

const Notice = styled(Typography)`
	color: #a4a4a4;
	padding: 0 0 1rem;
`;

export default BreakdownTab;
